import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, IconButton, Collapse, Card, CardContent,
    TextField, InputAdornment
} from '@mui/material';
import {
    KeyboardArrowDown as OpenIcon,
    KeyboardArrowUp as CloseIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import api from '../api/axiosInstance';
import { formatHumanReadableTimestamp } from '../utils/dateUtils';
import { useAuth } from '../Auth';

const AuditLog = () => {
    const { user } = useAuth();
    const [logs, setLogs] = useState([]);
    const [expandedRows, setExpandedRows] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await api.get('/auditlog');
                setLogs(response.data);
            } catch (err) {
                console.error("Failed to fetch audit logs", err);
            }
        };
        fetchLogs();
    }, []);

    // Toggle function for multiple rows
    const handleExpandRow = (id) => {
        setExpandedRows(prev =>
            prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
        );
    };

    const getActionColor = (action) => {
        // 1. Critical/Destructive Actions
        if (action.includes('DELETE')) return 'error';
        if (action.includes('FAILED_LOGIN_UNKNOWN_USER')) return 'error';

        // 2. Data Entry & Recovery
        if (action.includes('CREATE')) return 'success';
        if (action.includes('RESTORE')) return 'secondary';

        // 3. Modifications
        if (action.includes('UPDATE')) return 'warning';
        if (action.includes('FAILED_LOGIN_WRONG_PASSWORD')) return 'warning';

        // 4. System & Process Events
        if (action.includes('LOGIN')) return 'info';
        if (action.includes('IMPORT')) return 'info'; // Covers bulk_import

        return 'default';
    };

    const getDisplayTitle = () => {
        if (!user) return "Audit Log";

        // If the context is "Global View", we show "Global Audit Log"
        if (user.tenant_name === "Global View" || !user.tenant_id) {
            return "Global Audit Log";
        }

        // For any other tenant context (e.g., "Erina Scouts")
        return `Audit Log for ${user.tenant_name}`;
    };

    /**
     * Helper to extract a display name from the JSON values.
     */
    const getEntityDisplayName = (log) => {
        const data = log.new_value || log.delta_changes || log.old_value || {};

        // 1. If it's a User-related action (Login, Update User, Deactivate)
        // Show the username of the person being acted upon
        if (log.entity_type === 'USER') {
            return data.username || log.user_name || "System User";
        }

        // 2. If it's a Tenant action (Switch Context, Create Tenant)
        if (log.entity_type === 'TENANT') {
            return data.name || data.tenant_name || "Global Context";
        }

        // 3. Fallback for WWCC or other entities
        const name = data.surname
            ? `${data.first_given_name || ''} ${data.surname}`.trim()
            : data.name || log.entity_id?.slice(0, 8) || "N/A";

        return name;
    };
    
    const filteredLogs = useMemo(() => {
        if (!searchTerm) return logs;
        
        const lowSearch = searchTerm.toLowerCase();
        return logs.filter(log => {
            const entityName = getEntityDisplayName(log).toLowerCase();
            const userName = (log.user_name || '').toLowerCase();
            const action = (log.action || '').toLowerCase();
            const entityType = (log.entity_type || '').toLowerCase();

            return (
                userName.includes(lowSearch) ||
                action.includes(lowSearch) ||
                entityName.includes(lowSearch) ||
                entityType.includes(lowSearch)
            );
        });
    }, [logs, searchTerm]);

    return (
        <Box sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flexShrink: 0, mb: 2 }}>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4" >
                        {getDisplayTitle()}
                    </Typography>

                    <TextField
                        size="small"
                        placeholder="Search logs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                        sx={{ width: 350, bgcolor: 'white' }}
                    />
                </Box>

                {/* <Typography variant="h4" sx={{ mb: 2}}>{getDisplayTitle()}</Typography> */}

                <TableContainer component={Paper} elevation={3}>
                    <Table size="small">
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell width="50px" />
                                <TableCell>Timestamp</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Entity</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLogs.map((log) => {
                                const isExpanded = expandedRows.includes(log.id);
                                return (
                                    <React.Fragment key={log.id}>
                                        <TableRow hover>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => handleExpandRow(log.id)}>
                                                    {isExpanded ? <CloseIcon /> : <OpenIcon />}
                                                </IconButton>

                                                {/* <IconButton size="small" onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}>
                                                    {expandedRow === log.id ? <CloseIcon /> : <OpenIcon />}
                                                </IconButton> */}
                                            </TableCell>
                                            <TableCell>
                                                {/* This now shows "Wed, 8 Feb 2026 @ 10:21" */}
                                                {formatHumanReadableTimestamp(log.timestamp)}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{log.user_name}</Typography>
                                                <Typography variant="caption" color="text.secondary">ID: {log.user_id?.slice(0, 8)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={log.action} size="small" color={getActionColor(log.action)} variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {getEntityDisplayName(log)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">{log.entity_type}</Typography>
                                            </TableCell>
                                        </TableRow>

                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ margin: 2 }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            {/* Dynamic Labeling */}
                                                            {log.action.includes('UPDATE') ? "Changes (Delta)" :
                                                                log.action.includes('DELETE') ? "Deleted Record Data" : "Record Details"}
                                                        </Typography>

                                                        <Card variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                                                            <CardContent>
                                                                <pre style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', margin: 0 }}>
                                                                    {JSON.stringify(
                                                                        log.delta_changes || log.new_value || log.old_value || {},
                                                                        (key, value) => {
                                                                            // 1. HIDDEN FIELDS: Don't display the captcha token
                                                                            if (key === 'captchaToken') return undefined;

                                                                            // 2. Logic to reorder "from" and "to"
                                                                            if (value && typeof value === 'object' && !Array.isArray(value)) {
                                                                                if ('from' in value || 'to' in value) {
                                                                                    const reordered = {};
                                                                                    // Force 'from' to the top
                                                                                    if ('from' in value) reordered.from = value.from;
                                                                                    // Force 'to' second
                                                                                    if ('to' in value) reordered.to = value.to;

                                                                                    // Add any other keys that might exist in the object
                                                                                    Object.keys(value).forEach(k => {
                                                                                        if (k !== 'from' && k !== 'to') reordered[k] = value[k];
                                                                                    });
                                                                                    return reordered;
                                                                                }
                                                                            }

                                                                            // 3. If the value is a string and matches an ISO date pattern
                                                                            if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
                                                                                // We use the full human readable timestamp here for maximum detail
                                                                                return formatHumanReadableTimestamp(value);
                                                                            }
                                                                            return value;
                                                                        },
                                                                        2
                                                                    )}
                                                                </pre>

                                                                <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #eee' }}>
                                                                    {/* Showing the event's actual log time at the bottom of the card as well */}
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        <strong>Full Event Logged:</strong> {formatHumanReadableTimestamp(log.timestamp)}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        <strong>User Agent:</strong> {log.user_agent}
                                                                    </Typography>
                                                                </Box>
                                                            </CardContent>
                                                        </Card>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default AuditLog;